import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";

export interface Profile {
  phoneNumber: string;
  address?: {
    line1: string;
    line2?: string;
    state: string;
    city: string;
    zip: string;
    country: string;
  };
}

export const useGetProfile = (patientId?: string) => {
  return useApi<Profile, Profile>({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey: QueryKeys.Patient.MedicalRecords.Profile.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get("/charting/profile/", {
          params: { patientId },
        });
        return response?.data;
      },
    },
  });
};

interface UpdateProfilePhoneNumberRequest {
  patientId: string;
  phoneNumber: string;
}

export enum ProfileActionType {
  UPDATE_ADDRESS = "UPDATE_ADDRESS",
}

interface ProfileAvailableAction {
  action: ProfileActionType;
  allowed: boolean;
  notAllowedReason: string | null;
  notAllowedDebugMessage: string | null;
}

interface ProfileAvailableActionsResponse {
  availableActions?: ProfileAvailableAction[];
}

export const useUpdateProfilePhoneNumber = (
  patientId?: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  const updateProfilePhoneNumber = async ({
    phoneNumber,
  }: { phoneNumber: string }) => {
    await axiosInstance.put("/charting/profile/phone", {
      patientId,
      phoneNumber,
    });
  };

  return useApiMutation<void, UpdateProfilePhoneNumberRequest>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Profile.PHONE_NUMBER(patientId),
      mutationFn: updateProfilePhoneNumber,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Profile.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Demographics.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.Chart.Prescriptions.DEFAULT_PATIENT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

interface UpdateProfileAddressRequest {
  address: {
    addressLine_1: string;
    addressLine_2?: string;
    city: string;
    state: string;
    postalCode: string;
  };
}

export const useUpdateProfileAddress = (
  patientId?: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  const updateProfileAddress = async ({
    address,
  }: {
    address: UpdateProfileAddressRequest["address"];
  }) => {
    await axiosInstance.patch(`/clinic/patients/patient/${patientId}/address`, {
      address,
    });
  };

  return useApiMutation<void, UpdateProfileAddressRequest>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Profile.ADDRESS(patientId),
      mutationFn: updateProfileAddress,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Profile.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Demographics.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.Chart.Prescriptions.DEFAULT_PATIENT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

export const useProfileAvailableActions = (patientId?: string) => {
  return useApi<
    ProfileAvailableActionsResponse,
    Record<ProfileActionType, ProfileAvailableAction>
  >({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey:
        QueryKeys.Patient.MedicalRecords.Profile.AVAILABLE_ACTIONS(patientId),
      queryFn: async () => {
        const response =
          await axiosInstance.get<ProfileAvailableActionsResponse>(
            `/clinic/patients/patient/${patientId}/available-actions`,
          );
        return response?.data;
      },
      select: (data) => {
        const actions = data.availableActions || [];

        const result = actions.reduce(
          (acc, action) => {
            acc[action.action] = action;
            return acc;
          },
          {} as Record<ProfileActionType, ProfileAvailableAction>,
        );
        return result;
      },
    },
  });
};
